/**
 * description: 数据操作utils
 * author: 1580043700@qq.com
 * create_date: 2023/11/10-17:27
 */

let globalData
export const globalConfig = () => globalData

export const setGlobalConfig = props => {
    globalData = props
}

/**
 * 把接口数据转换成table组件数据
 * @param result
 * @param option total取值的key,records取值的key
 */
export const apiData2Table = (
    result: { total: number; records: Array<any> },
    option?: {
        total?: string
        records?: string
    }
): {
    total?: number
    records?: Array<any>
} => {
    const {total = 'count', records = 'list'} = option || {}
    return {
        total: result?.[total] ?? 0,
        records: result?.[records] ?? [],
    }
}

/**
 * 把接口数据转换成普通数据
 * @param result
 * @param defaultValue 默认值
 */
export const apiData2Data = (result: { data: { data: any } }, defaultValue?: any): any => {
    const {data} = result?.data ?? {}
    return data ?? defaultValue
}

/**
 * 把接口转换成table数据
 * @param apiFunc Promise分页接口
 * @param option 可选配置 {params: query 默认参数, data: body 默认参数, transformSearch: 转化查询数据的函数, transformResult:转化返回数据的函数}
 */
export const tableApiFunc = (
    apiFunc: (values: any) => any,
    option?: {
        params?: Record<string, any>
        data?: Record<string, any>
        paginationInBody?: boolean //分页参数是否放在body中
        fieldNames?: { current?: string; pageSize?: string; total?: string; records?: string }
        transformSearch?: (values: Record<string, any>) => Record<string, any>
        transformResult?: (values: Array<any>) => Array<any>
    }
): any => {
    return (values: any) => {
        return new Promise((resolve, reject) => {
            const {
                current = 'pageId',
                pageSize = 'pageSize',
                total = 'count',
                records = 'list',
            } = option?.fieldNames || {}
            const params = {
                ...option?.params,
                ...((option?.paginationInBody ?? false)
                    ? {}
                    : {[current]: values.current, [pageSize]: values.pageSize}),
            }
            apiFunc({
                params,
                data: {
                    ...option?.data,
                    ...((option?.paginationInBody ?? false)
                        ? {[current]: values.current, [pageSize]: values.pageSize}
                        : {}),
                    ...(option?.transformSearch ? option?.transformSearch(values.query) : values.query),
                },
            })
                .then((data: any) => {
                    const result: any = apiData2Table(data, {total, records})
                    resolve({
                        total: result.total,
                        records: option?.transformResult
                            ? option?.transformResult(result.records)
                            : result.records,
                    })
                })
                .catch(() => {
                    reject({success: false})
                })
        })
    }
}

/**
 * 把新枚举数据结构转换成以前的枚举数据结构
 * @param list 新枚举数据
 * @param option 默认值
 */
export const enumFormat = (
    list: Array<{ description: string; key: string }>,
    option?: {
        label?: string
        value?: string
    }
): Array<any> => {
    const {label = 'value', value = 'key'} = option || {}
    return (list || []).map(item => ({[label]: item?.description, [value]: item?.key}))
}

/**
 * 树形结构数据格式化
 * @param treeData 需要格式化的数据
 * @param option {children: children 名称, format: 格式化函数, emptyData: children为空时的值，默认为[]}
 */
export const treeFormat = (
    treeData: Array<Record<string, any>>,
    option?: {
        children?: string
        format?: (data: any, {index, level}: { index: number, level: number }) => any,
        childrenEmptyData?: [] | null | undefined
    } & Record<string, any>
): Array<any> => {
    const {children = 'children', format = (data: any) => data, level = 1, childrenEmptyData = []} = option || {};
    return (treeData || []).map((item, index) => {
        const list = item[children];
        return {
            ...format(item, {index, level}),
            [children]: list?.length ? treeFormat(list, {...option, level: level + 1}) : childrenEmptyData
        };
    })
}

/**
 * 数组字段名称进行转化
 * 如：listFormat([{name:'刘德华',value:'liudehua'}],{name:'label'}) 结果[{name:'刘德华',label:'刘德华',value:'liudehua'}]
 * @param list
 * @param key {旧key:'新key'}
 */
export const listFormat = (list: Array<Record<string, any>>, key: Record<string, any>) => {
    return (list || []).map(item => {
        const value = Object.assign({}, ...Object.keys(key || {}).map(k => ({[key[k]]: item[k]})))

        return {
            ...item,
            ...value,
        }
    })
}
/**
 * 移除字符串前后空格
 * @param str
 */
export const trimStr = (str: string) => {
    return str?.trim?.()
}

/**
 * 移除 [[]] 保留其中内容部分
 * @param str
 */
export const replaceRule = (str: string) => {
    return str?.replace(/\[\[([^\[\]]+)\]\]/g, '$1')
}

/**
 *  移除字符串包含中间部分空格
 * @param str
 */
export const trimStrAll = (str: string) => {
    return str?.replaceAll?.(' ', '')
}
/**
 * 移除对象中,string类型的空格
 * @param obj 需要移除空格的对象
 * @param trim trim：前后空格,all:包含中间部分空格
 */
export const trimObj = (obj: Record<string, any>, trim?: 'trim' | 'all') => {
    return Object.assign(
        {},
        ...Object.keys(obj).map(key => {
            let value = obj[key]
            if (typeof value === 'string') {
                value = trim === 'all' ? trimStrAll(value) : trimStr(value)
            }
            return {
                [key]: value,
            }
        })
    )
}

/**
 * 获取数据字典里面的单条数据
 * @param data
 * @param key
 */
export const getDictionaryData = (data: Array<any>, key: string) => {
    return (data || []).find(item => item?.priKey === key)?.children || []
}

/**
 * 根据 parentId 删除节点对象
 * @param data 树形数据
 * @param parentId 父级id
 * @param id
 * @param idName
 * @param childrenName 想要删除的字段
 */
export const deleteObjNodeById = (data: any[], parentId, id, idName = 'id', childrenName = 'children') => {
    for (let i = 0; i < data.length; i++) {
        // 首级删除自身
        if (parentId === '0' && data[i][idName] === id) {
            data.splice(i, 1)
            return
        }
        if (data[i][idName] === parentId) {
            // 当前父级多子级 删除当前子级
            const childItem = data[i][childrenName]
            for (let ci = 0; ci < childItem.length; ci++) {
                if (childItem[ci][idName] === id) {
                    childItem.splice(ci, 1);
                    return;
                }
            }
            return
        }
        if (data[i][childrenName]) {
            deleteObjNodeById(data[i][childrenName], parentId, id, idName, childrenName)
        }
    }
}

/**
 * 对象排除空对象
 * @param {Object} obj
 * @returns { Object }
 */
export function emptyingFn(obj) {
    for (const key in obj) {
        if (typeof obj[key] == 'string') {
            obj[key] = obj[key].replace(/(^\s*)|(\s*$)/g, '')
        }
        if (
            obj[key] === '' ||
            obj[key] == undefined ||
            obj[key] == null ||
            JSON.stringify(obj[key]) == '[]' ||
            JSON.stringify(obj[key]) == 'null'
        ) {
            delete obj[key]
        }
    }
    return obj
}
